import api from '../api';

// MIGRATION: Independent agents now use the unified /itineraries endpoints.
// Per decision, itineraries can no longer be created standalone - they are
// only created by converting a quote (convertFromQuote), matching how
// agency agents work. The unified schema also uses different field names
// and has no destination/adults/children columns, so those are adapted or
// dropped here rather than in every component that reads/writes an
// itinerary.
const STATUS_TO_LEGACY = { active: 'confirmed', upcoming: 'pending', completed: 'completed', cancelled: 'cancelled' };
const STATUS_TO_UNIFIED = { confirmed: 'active', pending: 'upcoming', completed: 'completed', cancelled: 'cancelled' };

const fromUnifiedItinerary = (itinerary) => {
  if (!itinerary || typeof itinerary !== 'object') return itinerary;
  const totalAmount = parseFloat(itinerary.total_amount) || 0;
  const totalPaid = parseFloat(itinerary.total_paid) || 0;
  return {
    ...itinerary,
    trip_name: itinerary.title,
    start_date: itinerary.travel_start_date,
    end_date: itinerary.travel_end_date,
    total_price: itinerary.total_amount,
    amount_paid: itinerary.total_paid,
    balance_due: totalAmount - totalPaid,
    notes: itinerary.notes ?? itinerary.agent_notes,
    status: STATUS_TO_LEGACY[itinerary.status] || itinerary.status
  };
};

const toUnifiedItinerary = (itineraryData) => {
  const { trip_name, start_date, end_date, total_price, notes, status, ...rest } = itineraryData;
  const mapped = { ...rest };
  if (trip_name !== undefined) mapped.title = trip_name;
  if (start_date !== undefined) mapped.travel_start_date = start_date;
  if (end_date !== undefined) mapped.travel_end_date = end_date;
  if (total_price !== undefined) mapped.total_amount = total_price;
  if (notes !== undefined) mapped.agent_notes = notes;
  if (status !== undefined) mapped.status = STATUS_TO_UNIFIED[status] || status;
  return mapped;
};

export const independentItinerariesService = {
  getAll: async () => {
    const response = await api.get('/itineraries');
    const list = Array.isArray(response.data) ? response.data : (response.data?.data || []);
    return list.map(fromUnifiedItinerary);
  },

  getById: async (id) => {
    const response = await api.get(`/itineraries/${id}`);
    return fromUnifiedItinerary(response.data);
  },

  update: async (id, itineraryData) => {
    const response = await api.put(`/itineraries/${id}`, toUnifiedItinerary(itineraryData));
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/itineraries/${id}`);
    return response.data;
  },

  // Itineraries are created by converting a quote, matching agency agents.
  convertFromQuote: async (quoteId) => {
    const response = await api.post(`/itineraries/convert-quote/${quoteId}`);
    return response.data;
  },

  // Records a client payment against an itinerary and recalculates its
  // total_paid/payment_status server-side.
  addPayment: async (itineraryId, amount, options = {}) => {
    const response = await api.post(`/itineraries/${itineraryId}/client-payments`, {
      payment_date: options.payment_date || new Date().toISOString().slice(0, 10),
      amount,
      payment_type: options.payment_type || 'partial',
      payment_method: options.payment_method || 'other',
      notes: options.notes
    });
    return response.data;
  }
};

export default independentItinerariesService;
